import React, {
  useEffect,
  useState,
  type ReactNode,
  type ReactElement,
  useReducer,
  useMemo,
  useCallback,
} from 'react';
import { dfReducer } from './reducer';

import { getConfig } from '../../utils/config';
import { CACHE_TIME } from '../../constants';
import { DFContext } from '../df-context';
import { initialResultsState } from '../state';
import { search } from '../../api/search';
import { useInfiniteQuery } from '@tanstack/react-query';
import { SET_SELECTED, UPDATE_STATE } from '../actions';
import { useLoader } from '../../hooks/use-loader';
import type { IResponse, ISearch } from '../../types';
import { mergeFilterAttributesReset } from '../../utils/filter-attributes';

let initialData: IResponse<ISearch> | null = null;

export function DfFilterProvider({
  children,
  open,
  appState,
  dispatchState,
}: {
  children: ReactNode;
  open: boolean;
  appState: any;
  dispatchState: any;
}): ReactElement {
  const [state, dispatch] = useReducer(dfReducer, initialResultsState);
  const { selected, params, itemFilter, shareIds } = state;
  const pimLanguage = getConfig('pim_language', 'en');
  const cmsNativeLanguage = getConfig('cms_native_language', 'EN-EN');

  const setSelected = useCallback((depth: number, id: string | number) => {
    dispatch({
      type: SET_SELECTED,
      depth,
      id: typeof id === 'string' ? parseInt(id, 10) : id,
    });
  }, []);

  const updateState = useCallback(() => {
    dispatch({
      type: UPDATE_STATE,
      appState,
    });
  }, [appState]);

  useEffect(() => {
    if (open) {
      // sync app state with filter modal state on open
      updateState();
    }
  }, [open, updateState]);

  const { data, isLoading } = useInfiniteQuery({
    // same query key as in result provider to avoid fetching data twice
    queryKey: ['search', appState.term, selected, itemFilter, shareIds.join('-')],
    queryFn: async () => {
      if (
        !initialData &&
        !(
          appState.term === '' &&
          selected &&
          Object.keys(selected).length === 0 &&
          itemFilter &&
          Object.keys(itemFilter).length === 0
        )
      ) {
        initialData = await search({
          cmsNativeLanguage,
          pimLanguage,
          rows: 0,
        });
      }

      const params = Object.entries(selected).map(([depth, pimId]) => ({
        depth: parseInt(depth, 10),
        pimId: pimId,
      }));

      const data = await search({
        cmsNativeLanguage,
        pimLanguage,
        term: appState.term,
        params,
        filterType: itemFilter?.fileType,
        filterLanguage: itemFilter?.language,
        searchObject: appState.preFilter,
        filterVariant: itemFilter?.variants,
        rows: 0,
      });

      return mergeFilterAttributesReset(initialData, data);
    },
    initialPageParam: 1,
    staleTime: Infinity,
    gcTime: CACHE_TIME,
    getNextPageParam: () => null,
  });

  const [newstate, setNewState] = useState<any>(state);

  useEffect(() => {
    if (data?.pages[0]?.data) {
      const { data: result } = data.pages[0];
      setNewState({
        results: result.searchResult.searchResult,
        totalDownloads: result.searchResult.numFound || 0,
        selectionAmount: result.searchResult.numFound || 0,
        countedCategories: result.categoryFilterResult.countedCategories || [],
        filterAttributes: result.categoryFilterResult.filter || {
          fileTypes: [],
          languages: [],
        },
      });
    }
  }, [data]);

  const showLoader = useLoader(open ? isLoading : false);

  const dispatchResult = useCallback(() => {
    dispatchState({ selected, params, itemFilter, shareIds });
  }, [dispatchState, itemFilter, params, selected, shareIds]);

  const value = useMemo(
    () => ({
      state: {
        ...state,
        ...newstate,
        attributes: appState.attributes,
        isLoading: showLoader,
      },
      dispatch,
      setSelected,
      dispatchResult,
      updateState,
    }),
    [appState.attributes, dispatchResult, newstate, setSelected, showLoader, state, updateState],
  );

  return <DFContext.Provider value={value}>{children}</DFContext.Provider>;
}
